import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Dispatch } from 'redux'
import { ComboWizardData, ComboWizardProps } from './combo-wizard-props'
import { AllCardsType } from '../../../data/all-cards'
import { Zodiaco, ZodiacoType } from '../../../data/zodiaco'
import {copyResponse,
    fotoCartas,
    getMazo,
    getMazoName,
    getTapiz,
    getZodiac,
    GetPersonalidad,
    isEmpty,
    selectCards,
    setMezcla, formatSignAndAge} from '../../../utils/functions'
import { ComboPreviousStep } from '../../../utils/combo-context'
import { GetConsult, ResetConsult, SaveAllCards } from '../../../store/actions'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { ButtonNormal } from '../../../components/buttons/button-normal'
import { ButtonReinterpret } from '../../../components/buttons/button-reinterpret'
import { getComboCardLayout } from './combo-card-layouts'
import MarqueeText from '../../../components/MarqueeText'
import { NombreTarotista, Personalidades } from '../../../data/personality'
import { LOVE_TIPOS_NEED_CONSULTADO } from '../flexible/spread-registry'

const createComboId = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return `combo-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const ComboWizard = ({
    comboName,
    steps,
    bgClass,
    interpretStep,
    consultaPlaceholder = 'Inquietud del consultante (opcional). Se usará en las tiradas del combo.',
    allowAnyLevel = false,
    onNuevoCombo,
    initialData,
}: ComboWizardProps) => {
    const navigate = useNavigate()
    const dispatch: Dispatch<any> = useDispatch()
    const emptyData: ComboWizardData = {
        consultante: { name: '', date: '', zodiac: '', phone: '', personality: '' },
        consultado: { name: '', date: '', zodiac: '' },
        consulta: '',
        tipo: '',
    }
    const [data, setData] = useState<ComboWizardData>(() => ({
        ...emptyData,
        ...initialData,
        consultante: { ...emptyData.consultante, ...initialData?.consultante },
        consultado: { ...emptyData.consultado!, ...initialData?.consultado },
        consulta: initialData?.consulta ?? '',
        tipo: initialData?.tipo ?? '',
    }))
    const [copia, setCopia]: any = useState(false)
    const { allCards, user, consult, loading } = useSelector((state: any) => state)
    const [response, setResponse] = useState('')
    const [selectedCards, setSelectedCards] = useState<AllCardsType[]>([])
    const [showResponse, setShowResponse] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)
    const [comboId] = useState(createComboId)
    const [stepResults, setStepResults] = useState<ComboPreviousStep[]>([])
    const [comboFinished, setComboFinished] = useState(false)
    const [status, setStatus] = useState({
        isDone: false,
        mezcladas: false,
        repartidas: false,
        isConsulting: false,
        isInterpret: false,
    })
    const zodiaco: ZodiacoType[] = Zodiaco
    const [personalidad, setPersonalidad] = useState<NombreTarotista>()

    const totalSteps = steps.length
    const stepConfig = steps[currentStep - 1]
    const cardCount = stepConfig.cardCount ?? 10
    const { Photo: StepPhotoCards, Show: StepShowCards } = getComboCardLayout(
        stepConfig.spreadId,
        cardCount
    )
    const needsPersonalityNumerology = useMemo(
        () => steps.some((s) => s.requiresPersonalityNumerology),
        [steps]
    )
    const stepNeedsPersonality = stepConfig.requiresPersonalityNumerology ?? false
    const needsConsultado =
        !!(stepConfig.needsConsultado ||
            (stepConfig.isLoveMultimodal && LOVE_TIPOS_NEED_CONSULTADO.includes(data.tipo || '')))

    // Si un paso posterior exige numerología y la fecha ya estaba cargada sin personality, calcularla
    useEffect(() => {
        if (!stepNeedsPersonality) return
        const date = data.consultante.date
        if (!date || data.consultante.personality) return
        if (parseInt(date.slice(0, 4), 10) <= 1900) return
        const personality = GetPersonalidad(date)
        setData((prev) => ({
            ...prev,
            consultante: { ...prev.consultante, personality },
        }))
    }, [
        currentStep,
        stepNeedsPersonality,
        data.consultante.date,
        data.consultante.personality,
    ])

    const hechoBloqueadoPor =
        data.consultante.name.length === 0
            ? 'Falta el nombre del consultante.'
            : data.consultante.date.length === 0
              ? 'Falta la fecha de nacimiento del consultante.'
              : stepNeedsPersonality && !data.consultante.personality
                ? 'Falta el número de personalidad (se calcula solo con la fecha).'
                : stepConfig.isLoveMultimodal && !data.tipo
                  ? 'Elegí la modalidad de la Tirada del Amor.'
                  : needsConsultado && !(data.consultado?.name || '').length
                    ? 'Falta el nombre del consultado / otra persona.'
                    : stepConfig.needsConsultado && !(data.consultado?.date || '').length
                      ? 'Falta la fecha de nacimiento del consultado.'
                      : null

    const handleZodiacConsultado = (date: string): void => {
        const zodiac = getZodiac(date)
        const personality = GetPersonalidad(date)
        setData({
            ...data,
            consultado: {
                name: data.consultado?.name || '',
                date,
                zodiac,
                personality,
            },
        })
    }

    const handleInputConsultado = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.name === 'date' && parseInt(event.target.value.slice(0, 4)) > 1900) {
            handleZodiacConsultado(event.target.value)
        } else {
            setData({
                ...data,
                consultado: {
                    name: data.consultado?.name || '',
                    date: data.consultado?.date || '',
                    zodiac: data.consultado?.zodiac || '',
                    ...data.consultado,
                    [event.target.name]: event.target.value,
                },
            })
        }
    }

    const resetStepState = () => {
        setStatus({
            isDone: false,
            mezcladas: false,
            repartidas: false,
            isConsulting: false,
            isInterpret: false,
        })
        setShowResponse(false)
        setResponse('')
        setSelectedCards([])
        dispatch(ResetConsult())
    }

    const reiniciarCombo = () => {
        if (onNuevoCombo) {
            onNuevoCombo(data)
            return
        }
        setStepResults([])
        setCurrentStep(1)
        setComboFinished(false)
        setData({
            consultante: { name: '', date: '', zodiac: '', phone: '', personality: '' },
            consultado: { name: '', date: '', zodiac: '' },
            consulta: '',
            tipo: '',
        })
        resetStepState()
    }

    const handleStatus = (name: string, value: boolean): void => {
        setStatus({ ...status, [name]: value })
    }

    const handleZodiacConsultant = (date: string): void => {
        const zodiac = getZodiac(date)
        const personality = needsPersonalityNumerology ? GetPersonalidad(date) : data.consultante.personality
        setData({
            ...data,
            consultante: {
                ...data.consultante,
                zodiac,
                date,
                ...(needsPersonalityNumerology ? { personality } : {}),
            },
        })
    }

    const handleInputConsultant = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.name === 'date' && parseInt(event.target.value.slice(0, 4)) > 1900) {
            handleZodiacConsultant(event.target.value)
        } else {
            setData({
                ...data,
                consultante: { ...data.consultante, [event.target.name]: event.target.value },
            })
        }
    }

    const handleConsult = (
        event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>
    ): void => {
        setData({ ...data, [event.target.name]: event.target.value })
    }

    const mezcla = () => {
        const mezcladas = setMezcla(allCards)
        dispatch(SaveAllCards(mezcladas))
        handleStatus('mezcladas', true)
    }

    const interpretar = () => {
        handleStatus('isConsulting', true)
        const info = interpretStep(
            currentStep,
            selectedCards,
            data,
            user.username,
            personalidad!,
            stepResults,
            comboId
        )
        dispatch(GetConsult(info))
    }

    const guardarPasoActual = (supervisedResponse: string) => {
        const result: ComboPreviousStep = {
            step: currentStep,
            spreadTitle: stepConfig.shortTitle,
            cards: selectedCards,
            response: supervisedResponse,
        }
        setStepResults((prev) => [...prev.filter((s) => s.step !== currentStep), result])
    }

    const continuarSiguientePaso = () => {
        guardarPasoActual(response)
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1)
            setData((prev) => ({ ...prev, consulta: '' }))
            resetStepState()
        }
    }

    const restaurarPaso = (saved: ComboPreviousStep) => {
        setCurrentStep(saved.step)
        setSelectedCards(saved.cards)
        setResponse(saved.response)
        setShowResponse(false)
        setComboFinished(false)
        setData((prev) => ({ ...prev, consulta: '' }))
        setStatus({
            isDone: true,
            mezcladas: true,
            repartidas: true,
            isConsulting: false,
            isInterpret: true,
        })
        setStepResults((prev) => prev.filter((s) => s.step < saved.step))
        dispatch(ResetConsult())
    }

    /** Vuelve al paso anterior con cartas e interpretación guardadas (para copiar si se olvidaron). */
    const volverPasoAnterior = () => {
        if (comboFinished) {
            const saved = [...stepResults].sort((a, b) => b.step - a.step)[0]
            if (saved) restaurarPaso(saved)
            return
        }
        if (currentStep <= 1) return
        const saved = stepResults.find((s) => s.step === currentStep - 1)
        if (!saved) return
        restaurarPaso(saved)
    }

    const puedeVolverAtras =
        comboFinished
            ? stepResults.length > 0
            : currentStep > 1 && stepResults.some((s) => s.step === currentStep - 1)

    const finalizarCombo = () => {
        guardarPasoActual(response)
        setComboFinished(true)
        setShowResponse(false)
    }

    const copiarComboCompleto = () => {
        const allSteps = [...stepResults]
        if (status.isInterpret && response) {
            const exists = allSteps.some((s) => s.step === currentStep)
            const finalStep: ComboPreviousStep = {
                step: currentStep,
                spreadTitle: stepConfig.shortTitle,
                cards: selectedCards,
                response,
            }
            if (!exists) allSteps.push(finalStep)
            else {
                const idx = allSteps.findIndex((s) => s.step === currentStep)
                allSteps[idx] = finalStep
            }
        }
        allSteps.sort((a, b) => a.step - b.step)
        const text = allSteps
            .map(
                (s) =>
                    `=== ${comboName} — Paso ${s.step}/${totalSteps}: ${s.spreadTitle} ===\n\n${s.response}`
            )
            .join('\n\n')
        copyResponse(text)
    }

    const pasosCompletados = useMemo(() => {
        const completed = stepResults.map((s) => s.step)
        if (status.isInterpret && !comboFinished) completed.push(currentStep)
        return Array.from(new Set(completed)).sort()
    }, [stepResults, status.isInterpret, comboFinished, currentStep])

    useEffect(() => {
        if (status.mezcladas && !status.repartidas) {
            setSelectedCards(selectCards(allCards, cardCount))
            setStatus((s) => ({ ...s, repartidas: true }))
        }
    }, [allCards, status.mezcladas, status.repartidas, cardCount])

    useEffect(() => {
        // Ignorar lecturas de otra tirada/combo que hayan quedado en Redux
        if (isEmpty(consult)) return
        if (consult.comboId && consult.comboId !== comboId) return
        if (consult.type && !String(consult.type).startsWith('combo-') && !loading) return

        if (!loading) {
            setStatus((s) => ({ ...s, isConsulting: false, isInterpret: true }))
            setResponse(consult.response)
            setShowResponse(true)
        } else {
            setResponse(consult.response || '')
            setShowResponse(true)
        }
    }, [consult, loading, comboId])

    useEffect(() => {
        if (stepConfig.fixedLoveTipo) {
            setData((prev) => ({ ...prev, tipo: stepConfig.fixedLoveTipo! }))
        }
    }, [currentStep, stepConfig.fixedLoveTipo])

    useEffect(() => {
        const saved = localStorage.getItem('personality')
        if (Object.entries(user).length === 0 || isEmpty(saved)) {
            navigate('/')
        } else {
            setPersonalidad(saved as NombreTarotista)
        }
        resetStepState()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- inicialización solo al montar
    }, [])

    useEffect(() => {
        if (Object.entries(user).length === 0) {
            navigate('/')
            return
        }
        if (!allowAnyLevel && user.level !== 1) {
            navigate('/')
        }
    }, [user, navigate, allowAnyLevel])

    useEffect(() => {
        if (showResponse) {
            const signoConsultante = document.getElementById('consultante')
            if (signoConsultante && data.consultante.zodiac.length > 0) {
                const description = zodiaco.filter((signo) => signo.name === data.consultante.zodiac)
                signoConsultante.innerHTML = description[0].description
            }
        }
    }, [showResponse, data, zodiaco])

    useEffect(() => {
        if (copia) {
            const element = document.getElementById('photo')
            if (element) {
                fotoCartas(element, data.consultante.name)
                setCopia(false)
            }
        }
    }, [copia, data.consultante.name])

    const copiar = () => {
        localStorage.setItem('client', JSON.stringify(data.consultante))
    }

    const pegar = () => {
        const cliente = localStorage.getItem('client')
        if (cliente) {
            const clienteObj = JSON.parse(cliente)
            setData({
                ...data,
                consultante: {
                    name: clienteObj.name,
                    date: clienteObj.date,
                    zodiac: clienteObj.zodiac,
                    phone: clienteObj.phone || '',
                },
            })
        }
    }

    return (
        <div className={`relative w-full h-full min-h-screen bg-no-repeat bg-cover ${bgClass} mini:px-2 md:px-4`}>
            <p className="w-full py-3 italic font-bold text-center text-white mini:text-xl xl:text-4xl drop-shadow-lg">
                Combo: {comboName}
            </p>
            <p className="pb-3 text-center text-white mini:text-sm xl:text-lg drop-shadow-md">
                Paso {currentStep} de {totalSteps} — {stepConfig.title}
            </p>

            {comboFinished ? (
                <div className="max-w-2xl p-6 mx-auto rounded-lg bg-violetLight/70 shadow-card-horizontal">
                    <p className="mb-4 text-lg font-semibold text-center">Combo completado</p>
                    <p className="mb-4 text-sm text-center text-gray-600">
                        Las {totalSteps} tiradas fueron supervisadas y guardadas por separado.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <ButtonNormal caption={`Copiar las ${totalSteps} lecturas`} onClick={() => copiarComboCompleto()} />
                        <ButtonNormal
                            caption="Volver a la última tirada"
                            onClick={() => volverPasoAnterior()}
                            disabled={!puedeVolverAtras}
                        />
                        <ButtonNormal caption="Nuevo combo" onClick={() => reiniciarCombo()} />
                    </div>
                    <div className="flex flex-col gap-4 mt-6">
                        {stepResults
                            .sort((a, b) => a.step - b.step)
                            .map((s) => (
                                <div key={s.step} className="p-4 bg-white rounded-lg">
                                    <p className="mb-2 font-semibold">
                                        Paso {s.step}: {s.spreadTitle}
                                    </p>
                                    <p className="text-sm whitespace-pre-wrap text-gray-700">{s.response}</p>
                                </div>
                            ))}
                    </div>
                </div>
            ) : (
                <div className="flex w-full mini:flex-col xl:flex-row">
                    <div className="flex flex-col h-full gap-3 rounded-lg mini:w-full xl:w-1/2 bg-violetLight/70 mini:px-2 mini:py-5 md:p-5 shadow-card-horizontal">
                        <div className="flex gap-2 justify-center pb-2">
                            {steps.map((s) => (
                                <span
                                    key={s.step}
                                    className={`px-3 py-1 text-xs rounded-full ${
                                        pasosCompletados.includes(s.step)
                                            ? 'bg-violetDark text-white'
                                            : s.step === currentStep
                                              ? 'bg-white text-violetDark border-2 border-violetDark'
                                              : 'bg-gray-200 text-gray-500'
                                    }`}
                                >
                                    {s.step}
                                </span>
                            ))}
                        </div>

                        <div className="flex flex-col gap-3">
                            <div className="flex gap-3 mini:flex-col md:flex-row">
                                <input
                                    name="name"
                                    type="text"
                                    value={data.consultante.name}
                                    onChange={(e) => handleInputConsultant(e)}
                                    placeholder="Nombre del Consultante"
                                    className="h-10 pl-3 text-base text-gray-800 bg-white rounded-lg mini:w-full md:w-1/2 focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2 disabled:bg-white disabled:text-gray-800"
                                    autoFocus={true}
                                    disabled={currentStep > 1 || status.repartidas}
                                />
                                <div className="flex gap-3">
                                    <input
                                        type="date"
                                        name="date"
                                        value={data.consultante.date}
                                        onChange={(e) => handleInputConsultant(e)}
                                        placeholder="Nacimiento"
                                        className="w-1/2 h-10 pl-3 text-base text-gray-800 bg-white rounded-lg focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2 disabled:bg-white disabled:text-gray-800"
                                        disabled={currentStep > 1 || status.repartidas}
                                    />
                                    <input
                                        type="text"
                                        name="zodiac"
                                        value={formatSignAndAge(data.consultante.zodiac, data.consultante.date)}
                                        readOnly={true}
                                        className="w-1/2 h-10 px-3 text-base text-gray-800 bg-white rounded-lg disabled:bg-white disabled:text-gray-800"
                                        disabled={true}
                                    />
                                </div>
                                <div className="flex items-center gap-1 text-white">
                                    <button
                                        className="p-2 rounded-full bg-violetDark hover:cursor-pointer disabled:bg-slate-400 disabled:text-slate-300 disabled:cursor-not-allowed"
                                        onClick={() => copiar()}
                                        title="Copiar"
                                        disabled={
                                            currentStep > 1 ||
                                            data.consultante.name.length === 0 ||
                                            data.consultante.zodiac.length === 0
                                        }
                                    >
                                        <ContentCopyIcon />
                                    </button>
                                    <button
                                        className="p-2 rounded-full bg-violetDark hover:cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed"
                                        onClick={() => pegar()}
                                        title="Pegar"
                                        disabled={currentStep > 1 || status.repartidas}
                                    >
                                        <ContentPasteIcon />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {stepConfig.fixedLoveTipo && (
                            <div className="p-3 text-sm bg-white rounded-lg text-gray-700">
                                <p className="font-semibold text-violetDark">Modalidad — Tirada del Amor</p>
                                <p>{stepConfig.loveTipoLabel ?? 'Amor para Solteros'}</p>
                                <p className="mt-1 text-xs text-gray-500">
                                    Definida por este combo. No hace falta elegir entre las opciones de la tirada
                                    del amor.
                                </p>
                            </div>
                        )}

                        {stepConfig.isLoveMultimodal && (
                            <div className="flex flex-col gap-1 p-3 text-sm bg-white rounded-lg text-gray-700">
                                <p className="mb-1 font-semibold text-violetDark">Modalidad — Tirada del Amor</p>
                                {[
                                    { value: '1', label: 'Amor (Sólo una persona)' },
                                    { value: '2', label: 'Vínculo (Dos personas)' },
                                    { value: '3', label: 'Amor / Pareja (esquema clásico)' },
                                    { value: '4', label: 'Amor para Solteros' },
                                    { value: '5', label: 'Amor para cuando se estén conociendo' },
                                    { value: '6', label: 'Amor para gente en Pareja' },
                                ].map((opt) => (
                                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="tipo"
                                            value={opt.value}
                                            checked={data.tipo === opt.value}
                                            onChange={(e) => handleConsult(e)}
                                            disabled={status.repartidas}
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                        )}

                        {needsConsultado && (
                            <div className="flex flex-col gap-2 p-3 bg-white rounded-lg">
                                <p className="text-sm font-semibold text-violetDark">Consultado / otra persona</p>
                                <input
                                    name="name"
                                    type="text"
                                    value={data.consultado?.name || ''}
                                    onChange={(e) => handleInputConsultado(e)}
                                    placeholder="Nombre del Consultado"
                                    className="h-10 pl-3 text-base text-gray-800 bg-white rounded-lg w-full focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2 disabled:bg-white disabled:text-gray-800"
                                    disabled={status.repartidas}
                                />
                                <div className="flex gap-3">
                                    <input
                                        type="date"
                                        name="date"
                                        value={data.consultado?.date || ''}
                                        onChange={(e) => handleInputConsultado(e)}
                                        title={
                                            stepConfig.needsConsultado
                                                ? 'Fecha de nacimiento del consultado'
                                                : 'Fecha de nacimiento (opcional)'
                                        }
                                        className="w-1/2 h-10 pl-3 text-base text-gray-800 bg-white rounded-lg focus-visible:outline-none focus-visible:border-violetDark focus-visible:border-2 disabled:bg-white disabled:text-gray-800"
                                        disabled={status.repartidas}
                                    />
                                    <input
                                        type="text"
                                        name="zodiac"
                                        value={formatSignAndAge(data.consultado?.zodiac || '', data.consultado?.date || '')}
                                        readOnly
                                        disabled
                                        placeholder="Signo"
                                        className="w-1/2 h-10 px-3 text-base text-gray-800 bg-white rounded-lg disabled:bg-white disabled:text-gray-800"
                                    />
                                </div>
                                {stepConfig.isLoveMultimodal && (
                                    <p className="text-xs text-gray-500">
                                        El nombre es obligatorio. La fecha de nacimiento es opcional.
                                    </p>
                                )}
                            </div>
                        )}

                        <div>
                            <textarea
                                name="consulta"
                                value={data.consulta}
                                className="p-3 h-[30vh] rounded-lg text-base text-gray-800 bg-white w-full resize-none"
                                placeholder={consultaPlaceholder}
                                onChange={(e) => handleConsult(e)}
                            />
                        </div>

                        {stepResults.length > 0 && (
                            <div className="p-3 text-xs bg-white rounded-lg text-gray-600">
                                <p className="mb-1 font-semibold">Lecturas anteriores del combo (contexto para la IA):</p>
                                {stepResults
                                    .sort((a, b) => a.step - b.step)
                                    .map((s) => (
                                        <p key={s.step}>
                                            ✓ Paso {s.step}: {s.spreadTitle}
                                        </p>
                                    ))}
                            </div>
                        )}

                        <div className="flex flex-wrap justify-center gap-3">
                            {!status.isDone && hechoBloqueadoPor && (
                                <p className="w-full px-3 py-2 text-sm text-center text-red-700 bg-white rounded-lg font-medium">
                                    {hechoBloqueadoPor}
                                </p>
                            )}
                            <ButtonNormal
                                caption="Hecho"
                                onClick={() => handleStatus('isDone', true)}
                                disabled={status.isDone || !!hechoBloqueadoPor}
                            />
                            <ButtonNormal
                                caption="Repartir"
                                onClick={() => mezcla()}
                                disabled={!status.isDone || status.repartidas}
                            />
                            <ButtonNormal
                                caption="Editar"
                                onClick={() => handleStatus('isDone', false)}
                                disabled={!status.isDone || status.repartidas}
                            />
                            <ButtonNormal
                                caption="Hacer foto de cartas"
                                onClick={() => setCopia(true)}
                                disabled={!status.repartidas || status.isConsulting}
                            />
                            <ButtonNormal
                                caption={status.isConsulting ? 'Interpretando' : 'Interpretar'}
                                onClick={() => interpretar()}
                                disabled={!status.repartidas || status.isInterpret}
                                loading={loading}
                            />
                            <ButtonReinterpret
                                isConsulting={status.isConsulting}
                                onConfirm={() => interpretar()}
                                disabled={!status.isInterpret}
                                loading={loading}
                            />
                            <ButtonNormal
                                caption="Ver Respuesta"
                                onClick={() => setShowResponse(true)}
                                disabled={response?.length === 0}
                            />
                            {puedeVolverAtras && (
                                <ButtonNormal
                                    caption={`Volver a tirada ${currentStep - 1}`}
                                    onClick={() => volverPasoAnterior()}
                                />
                            )}
                            {currentStep < totalSteps && (
                                <ButtonNormal
                                    caption={`Continuar con tirada ${currentStep + 1}`}
                                    onClick={() => continuarSiguientePaso()}
                                    disabled={!status.isInterpret}
                                />
                            )}
                            {currentStep === totalSteps && (
                                <ButtonNormal
                                    caption="Finalizar combo"
                                    onClick={() => finalizarCombo()}
                                    disabled={!status.isInterpret}
                                />
                            )}
                        </div>
                        <div className="w-full font-semibold text-red-400 bg-black h-[24px]">
                            <MarqueeText
                                text={'Estás usando el mazo de cartas: ' + getMazoName(user.tarot)}
                                speed={15}
                            />
                        </div>
                    </div>

                    {selectedCards.length > 0 ? (
                        <div className="relative mini:w-full xl:w-1/2">
                            <StepShowCards selectedCards={selectedCards} mazo={getMazo(user.tarot)} />
                        </div>
                    ) : null}

                    {selectedCards.length > 0 ? (
                        <div
                            className={`absolute top-0 left-0 w-screen h-screen flex justify-center items-center ${
                                copia ? 'block' : 'hidden'
                            }`}
                        >
                            <div className="w-fit h-fit" id="photo">
                                <StepPhotoCards
                                    cards={selectedCards}
                                    mazo={getMazo(user.tarot)}
                                    tapiz={getTapiz(user.tarot)}
                                />
                            </div>
                        </div>
                    ) : null}

                    {showResponse ? (
                        <div className="absolute mini:w-[96vw] md:w-[90vw] h-[90vh] top-[2%] mini:left-[2vw] md:left-[5%] bg-zinc-200 shadow-2xl flex flex-row gap-5 rounded-xl mini:p-2 md:p-5 z-50">
                            <div className="flex flex-col gap-3 mini:w-full xl:w-2/3">
                                <p className="font-semibold text-violetDark">
                                    Paso {currentStep}/{totalSteps}: {stepConfig.shortTitle}
                                </p>
                                <textarea
                                    className="w-full h-full p-5 rounded-lg resize-none"
                                    value={response}
                                    onChange={(e) => setResponse(e.target.value)}
                                />
                                <div className="flex flex-row flex-wrap justify-center gap-1">
                                    <ButtonNormal
                                        caption="Volver"
                                        onClick={() => setShowResponse(false)}
                                        disabled={loading}
                                    />
                                    <ButtonNormal
                                        caption="Copiar Respuesta"
                                        onClick={() => copyResponse(response)}
                                        disabled={loading}
                                    />
                                    <ButtonNormal
                                        caption="Hacer foto de cartas"
                                        onClick={() => {
                                            setShowResponse(false)
                                            setCopia(true)
                                        }}
                                        disabled={loading || selectedCards.length === 0}
                                    />
                                    {puedeVolverAtras && (
                                        <ButtonNormal
                                            caption={`Volver a tirada ${currentStep - 1}`}
                                            onClick={() => volverPasoAnterior()}
                                            disabled={loading}
                                        />
                                    )}
                                    {currentStep < totalSteps ? (
                                        <ButtonNormal
                                            caption={`Continuar con tirada ${currentStep + 1}`}
                                            onClick={() => {
                                                setShowResponse(false)
                                                continuarSiguientePaso()
                                            }}
                                            disabled={loading || !status.isInterpret}
                                        />
                                    ) : (
                                        <ButtonNormal
                                            caption="Finalizar combo"
                                            onClick={() => finalizarCombo()}
                                            disabled={loading || !status.isInterpret}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col w-1/3 gap-3 mini:hidden xl:block">
                                <p className="font-semibold">Consultante: {data.consultante.zodiac}</p>
                                <div
                                    className="w-full p-5 overflow-y-auto text-sm rounded-lg bg-zinc-50"
                                    id="consultante"
                                ></div>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}

            {personalidad && !comboFinished && (
                <div className="fixed z-50 top-[7vh] right-4 animate-fadeIn">
                    <div className="relative">
                        <div className="absolute inset-0 z-0 -m-1.5 rounded-full pointer-events-none animate-spin-subtle">
                            <div className="w-full h-full rounded-full bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-200 p-[1px]"></div>
                        </div>
                        <img
                            src={require('../../../assets' + Personalidades[personalidad].image)}
                            alt={Personalidades[personalidad].name}
                            className="relative z-10 object-cover w-20 h-20 transition-transform rounded-full shadow-2xl md:w-24 md:h-24 group-hover:scale-110"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default ComboWizard
